import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import GlpiEntityFields from "../GlpiEntityFields";
import TaskEntriesSummary from "../TaskEntriesSummary";

/**
 * Modal de "devolver para fila" para tickets com GLPI habilitado e interação
 * do atendente no ciclo atual — oferece registrar um chamado GLPI (que
 * permanece sempre aberto, já que o atendimento não terminou) ou devolver
 * sem abrir chamado nenhum.
 */
const GlpiReturnDialog = ({ open, onClose, ticket }) => {
	const navigate = useNavigate();

	const [glpiMode, setGlpiMode] = useState("new");
	const [glpiSolution, setGlpiSolution] = useState("");
	const [loading, setLoading] = useState(false);
	const [taskEntries, setTaskEntries] = useState([]);
	const [entityFields, setEntityFields] = useState({
		glpiEntityId: null,
		glpiCategoryId: null,
		glpiType: null,
		glpiClassificationId: null,
		glpiRequesterId: null,
		glpiActiontime: undefined,
		glpiServiceDate: "",
		glpiName: null,
		isValid: false
	});

	useEffect(() => {
		if (!open || !ticket) return;
		setGlpiMode("new");
		setGlpiSolution("");
		setTaskEntries([]);
		api.get(`/tickets/${ticket.id}/task-entries`)
			.then(({ data }) => setTaskEntries(Array.isArray(data) ? data : []))
			.catch(() => setTaskEntries([]));
	}, [open, ticket]);

	const handleConfirm = async () => {
		setLoading(true);
		try {
			if (glpiMode === "none") {
				await api.put(`/tickets/${ticket.id}`, { status: "pending", userId: null });
				onClose();
				navigate("/tickets");
				return;
			}

			const { glpiEntityId, glpiCategoryId, glpiType, glpiClassificationId, glpiRequesterId, glpiActiontime, glpiServiceDate, glpiName } = entityFields;
			await api.put(`/tickets/${ticket.id}`, {
				status: "pending",
				userId: null,
				...(glpiSolution.trim() ? { glpiSolution: glpiSolution.trim() } : {}),
				...(glpiCategoryId ? { glpiCategoryId } : {}),
				...(glpiType ? { glpiType } : {}),
				...(glpiActiontime ? { glpiActiontime } : {}),
				...(glpiEntityId ? { glpiEntityId } : {}),
				...(glpiServiceDate ? { glpiServiceDate } : {}),
				...(glpiClassificationId ? { glpiClassificationId } : {}),
				...(glpiName ? { glpiName } : {}),
				...(ticket.isGroup && glpiRequesterId ? { glpiRequesterId } : {})
			});
			onClose();
			navigate("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	if (!ticket) return null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Devolver para fila</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
				<RadioGroup row value={glpiMode} onChange={e => setGlpiMode(e.target.value)} sx={{ mt: 0.5 }}>
					<FormControlLabel value="new" control={<Radio size="small" />} label="Abrir chamado" />
					<FormControlLabel value="none" control={<Radio size="small" />} label="Não abrir chamado" />
				</RadioGroup>
				{glpiMode === "new" && (<>
					<TaskEntriesSummary entries={taskEntries} />
					<GlpiEntityFields
						open={open}
						ticket={ticket}
						onChange={setEntityFields}
						showCategoryFields
						taskEntries={taskEntries}
					/>
					<TextField
						autoFocus
						multiline
						rows={3}
						fullWidth
						variant="outlined"
						size="small"
						label="Observações (opcional)"
						placeholder="Descreva o que foi realizado até agora..."
						value={glpiSolution}
						onChange={e => setGlpiSolution(e.target.value)}
					/>
				</>)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={loading || (glpiMode === "new" && !entityFields.isValid)}
				>
					{loading
						? "Devolvendo..."
						: glpiMode === "none"
							? "Devolver sem chamado"
							: "Criar chamado (aberto) e devolver para fila"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default GlpiReturnDialog;
